/**
 * The prefixes of the platform-scoped design-token names (R-65). `@zion/design-tokens` declares
 * every `--ds-*` token and the deprecated `--zion-*` aliases; Tailwind's default theme declares
 * neither prefix, so a match is always a platform token.
 */
export const DESIGN_TOKEN_PREFIXES = ['--ds-', '--zion-'];
/**
 * The Tailwind-claimed names `@zion/design-tokens` declares (R-65). Tailwind's default theme shares
 * the `--color-*` and `--shadow-*` prefixes (`--color-red-500`, `--shadow-sm`), so these are named
 * one by one: an app's Tailwind defaults stay, and only the platform's own tokens go. The test in
 * `scope-css.test.ts` reads `tokens.css` and fails when this list and the file disagree.
 */
export const DESIGN_TOKEN_NAMES = new Set([
    '--color-surface',
    '--color-surface-elevated',
    '--color-surface-contrast',
    '--color-surface-muted',
    '--color-primary',
    '--color-secondary',
    '--color-muted',
    '--color-border-subtle',
    '--color-border-strong',
    '--color-accent',
    '--color-accent-secondary',
    '--color-accent-tertiary',
    '--color-state-critical',
    '--color-state-warning',
    '--color-state-success',
    '--color-state-success-on',
    '--color-state-info',
    '--shadow-card',
    '--shadow-border',
    '--shadow-elevated',
    '--shadow-overlay',
]);
/**
 * True when `name` is a custom property `@zion/design-tokens` declares (R-65). The match is exact
 * and case-sensitive, as custom property names are. The caller passes a decoded name: the scoper
 * decodes the escapes of a declaration name before it asks. Pure.
 */
export function isDesignTokenName(name) {
    return DESIGN_TOKEN_NAMES.has(name) || DESIGN_TOKEN_PREFIXES.some((prefix) => name.startsWith(prefix));
}
/**
 * The prefix of the `@property` names an app stylesheet may register (R-94). Tailwind v4 registers the
 * same `--tw-*` properties in every build, so an identical second registration changes nothing.
 */
export const PROPERTY_REGISTRATION_PREFIX = '--tw-';
/** The attribute every surface mount root carries (`markScopeRoot` in `css.ts`). */
const MOUNT_ROOT_ATTRIBUTE = '[data-hosted-app]';
/**
 * The lower bound every scoped selector carries on its subject (R-107):
 * `:not(<scope> [data-hosted-app]:not(<scope>) *)`. An element below the mount root of another app's
 * surface, where that root sits inside a `scopeSelector` root, matches none of this app's rules. The
 * nested mount root itself still matches, so the consumer keeps the box it gives an embedded
 * surface. A surface of the same app inside the scope (the root shell embeds its own `home.v1`) keeps
 * the app's rules, because its root matches `scopeSelector`. One form escapes the bound: an element of
 * the app below a surface of another app below the app's root takes none of the app's rules.
 * `scopeSelector` is the already-escaped selector {@link scopeAppCss} takes. Pure.
 */
export function nestedSurfaceBoundary(scopeSelector) {
    return `:not(${scopeSelector} ${MOUNT_ROOT_ATTRIBUTE}:not(${scopeSelector}) *)`;
}
/**
 * Scope an app stylesheet under `scopeSelector`, keep only the allowlisted constructs (R-90), strip
 * the platform's design tokens (R-65), bound every selector at nested surface roots (R-107) and
 * confine the document-global names (R-94). Returns the stylesheet text only;
 * {@link scopeAppCssWithRejections} also names what went.
 *
 * What stays, and how it is rewritten:
 *
 * - A style rule whose prelude is a plain selector list. Each selector becomes a descendant of the
 *   scope root (`.a` gives `[scope] .a`). A leading document-root compound (`:root`, `html`,
 *   `:host`, `:host(...)`, `body`) is replaced by the scope root (`body .prose` gives
 *   `[scope] .prose`). A leading polarity class (`.dark`, `.light`) stays as the ancestor, with the
 *   scope after it (`.dark .x` gives `.dark [scope] .x`). A root or polarity compound with a sibling
 *   combinator after it takes the descendant form (`body ~ *` gives `[scope] body ~ *`, which matches
 *   nothing), because mapped onto the scope root it would match the mount root's later siblings.
 *   Duplicate scoped selectors collapse into one.
 * - Each scoped selector then takes {@link nestedSurfaceBoundary} on its subject compound (R-107):
 *   before the subject's first pseudo-element (`::x`, or the legacy `:before`, `:after`,
 *   `:first-line`, `:first-letter`), else at its end. So `.a::before` gives
 *   `[scope] .a<boundary>::before`, and `:root` gives `[scope]<boundary>`. A selector that ends with a
 *   combinator goes as `selector`.
 * - The rule body keeps each declaration whose name is an ident followed by a colon, in its order. A
 *   custom property value may hold a `{}` block; any other value may not. A custom property name is a
 *   decoded name that starts with `--` and has more than two code units. The name `--` alone is
 *   reserved: the browser refuses it as a declaration and reads its text as a nested rule, so a `--`
 *   part with a `{}` block goes as a nested rule.
 * - In a rule whose every selector is a bare root compound or a bare polarity class, each
 *   declaration whose decoded name {@link isDesignTokenName} admits goes (R-65). Such a rule would
 *   otherwise declare the token on the mount root, where it beats the tenant accent the shell writes
 *   on `<html>`. A rule left with only whitespace goes whole, and {@link auditScopedCss} counts it as
 *   `stripped`.
 * - `@media`, `@supports`, `@container` and the block form of `@layer`, with their contents walked
 *   by these same rules; the `@layer` statement (`@layer a, b;`).
 * - `@keyframes`, `@font-face` and `@property`, with their blocks whole. A `@property` whose decoded
 *   name {@link isDesignTokenName} admits goes as an `at-rule` (R-65): a registration with
 *   `inherits: false` gives every element below `<html>` its initial value, the shell's included. A
 *   `@keyframes` whose ident or decoded string name is empty, or is `none`, `default` or a CSS-wide
 *   keyword in any ASCII case, goes as an `at-rule`: a rename would make it live under a name the
 *   browser drops (S5a WS-40 architecture-5, architecture-8).
 * - Unless `options.system` (R-94): a `@keyframes` name `<name>` (an ident or a string, decoded)
 *   becomes the escaped ident `<appId>_<name>`, one space after the at-keyword. Each `font-family` descriptor of an `@font-face`
 *   becomes the string `"<appId>_<family>"`, and the family of the last one is the kept family of the
 *   block, as the browser keeps the last descriptor; an `@font-face` that holds a `{}` block, or a
 *   `font-family` that is no family name (a generic family, `-webkit-body` included, or a CSS-wide keyword alone, or anything but
 *   one string or a run of idents), goes as an `at-rule`. In the kept style rules, the declarations
 *   `animation` and `animation-name` and every custom property rename each top-level ident or string
 *   that names a kept `@keyframes` of this stylesheet. The declarations `font-family` and every custom
 *   property rename each comma-separated family that names a kept `@font-face` family, compared in
 *   ASCII lower case, to the same string. The `important` of a trailing `!important` stays as written.
 *   Any other reference stays as written.
 * - Unless `options.system` (R-94): a `@property` stays only when its decoded name starts with
 *   {@link PROPERTY_REGISTRATION_PREFIX} and its registration text equals the registration of that name
 *   in `options.properties`, else the first one earlier in this stylesheet; else it goes as an `at-rule`. The
 *   registration text is the block's tokens with comments gone, whitespace runs as one space,
 *   whitespace beside a `:` or `;` gone, and trailing `;` gone.
 *
 * What goes: every other at-rule, an at-rule name written with an escape, a nested rule, a prelude
 * that is not a plain selector list, a body part that is not a declaration, a bad string, a bad url,
 * and a prelude with no block at the end of the input. A comment goes everywhere. Where its removal
 * would join two tokens into one, an empty comment stays between them (`url/**\/(` stays two tokens).
 * Whitespace at the edges of a prelude, a selector or a statement goes; whitespace inside a declaration
 * stays. A block the input never closes is closed at the end. No other byte changes.
 *
 * A nested rule goes whole, as `nested-rule`, and the declarations around it stay:
 * `.a{color:red;@supports (x){color:blue}}` keeps `color:red` alone. Production CSS is flat:
 * Tailwind's `optimize` step lowers CSS nesting in the build. The
 * `zionSurfacePreview` dev server lowers it the same way before the preview scopes a dev stylesheet
 * (#4070). Hand this function flat CSS.
 *
 * `scopeSelector` must be a valid, already-escaped selector (`[data-hosted-app="perf"]`). Pure,
 * never throws, and never writes to `options.properties`.
 */
export function scopeAppCss(cssText, scopeSelector, options) {
    return walkStylesheet(cssText, scopeSelector, options).css;
}
/**
 * {@link scopeAppCss}, plus every construct it dropped, in stylesheet order (R-90), plus the new
 * `@property` registrations it kept (R-94). `injectScopedCss` in `css.ts` turns each rejection into one
 * `surface:css-rejected` runtime event and adds the registrations to the document's. The design-token
 * strip (R-65) is no rejection. Pure, never throws.
 */
export function scopeAppCssWithRejections(cssText, scopeSelector, options) {
    const walk = walkStylesheet(cssText, scopeSelector, options);
    return { css: walk.css, rejections: walk.rejections, properties: walk.properties };
}
/**
 * Count the rule-level constructs of `css` as the allowlist reads them (#1292). A style rule with a
 * non-empty prelude counts one, kept or refused. A block at-rule counts one, kept or refused, except a
 * kept grouping at-rule, whose own rules count in its place. A statement, a rule body's contents and a
 * prelude with no block count nothing. So a refused construct counts one on the input side and
 * nothing on the output side. Pure.
 *
 * It reuses the scoper's own tokenizer and walk on purpose. A second parser would misread a different
 * input than the scoper does, and the pair would then disagree for reasons that have nothing to do
 * with the loss this detects.
 */
export function countStyleRules(css) {
    return walkStylesheet(css, '', null).rulesIn;
}
/**
 * Count the style rules {@link scopeAppCss} drops whole because the design-token strip emptied them
 * (R-65): a kept document-root or polarity rule whose every kept declaration was a platform token.
 * Same walk as {@link countStyleRules}. Pure.
 */
export function countTokenOnlyRules(css) {
    return walkStylesheet(css, '', null).stripped;
}
/** Audit one scoping pass: `input` is the app stylesheet, `output` what {@link scopeAppCss} made of it. Pure. */
export function auditScopedCss(input, output) {
    const inWalk = walkStylesheet(input, '', null);
    const rulesOut = countStyleRules(output);
    return {
        rulesIn: inWalk.rulesIn,
        rulesOut,
        stripped: inWalk.stripped,
        dropped: Math.max(0, inWalk.rulesIn - inWalk.stripped - rulesOut),
    };
}
/** The single-character tokens. */
const SINGLE_TOKENS = {
    '(': '(',
    ')': ')',
    '[': '[',
    ']': ']',
    '{': '{',
    '}': '}',
    ',': 'comma',
    ':': 'colon',
    ';': 'semicolon',
};
function token(type, start, end, value = '', clean = true) {
    return { type, start, end, value, clean };
}
/**
 * Tokenize `css` as CSS Syntax 3 §4.3.1 does, with every comment removed. A comment runs from `/*` to
 * the next `*\/`, or to the end of the input. The tokens keep their spans in `css`. Pure.
 */
function tokenize(css) {
    const tokens = [];
    let i = 0;
    while (i < css.length) {
        if (css.startsWith('/*', i)) {
            const end = css.indexOf('*/', i + 2);
            i = end === -1 ? css.length : end + 2;
            continue;
        }
        const next = readToken(css, i);
        tokens.push(next);
        i = next.end;
    }
    return tokens;
}
/** Read the one token that starts at `i`, a position where no comment starts (CSS Syntax 3 §4.3.1). Its end is above `i`. */
function readToken(css, i) {
    const ch = css[i];
    if (isCssWhitespace(ch)) {
        let j = i + 1;
        while (isCssWhitespace(css[j]))
            j += 1;
        return token('ws', i, j);
    }
    if (ch === '"' || ch === "'")
        return readStringToken(css, i);
    const single = SINGLE_TOKENS[ch];
    if (single !== undefined)
        return token(single, i, i + 1);
    if (ch === '#' && (isNameCodePoint(css[i + 1]) || isValidEscape(css, i + 1))) {
        const [name, end, cut] = readName(css, i + 1);
        return token('hash', i, end, name, !cut);
    }
    if (startsNumber(css, i))
        return readNumeric(css, i);
    if (css.startsWith('<!--', i))
        return token('cdo', i, i + 4);
    if (css.startsWith('-->', i))
        return token('cdc', i, i + 3);
    if (startsIdent(css, i))
        return readIdentLike(css, i);
    if (ch === '@' && startsIdent(css, i + 1)) {
        const [name, end, cut] = readName(css, i + 1);
        return token('at-keyword', i, end, name, !cut);
    }
    const delim = String.fromCodePoint(css.codePointAt(i));
    return token('delim', i, i + delim.length, delim, delim !== '\\');
}
/**
 * Read a string token from the quote at `i` (CSS Syntax 3 §4.3.5). The matching quote ends it. An
 * unescaped newline ends it as a bad string, and the newline is not part of it. A `\` before a
 * newline (`\r\n` counts as one) continues the string; any other `\` starts an escape. The end of the
 * input ends it as an unclean string.
 */
function readStringToken(css, i) {
    const quote = css[i];
    let j = i + 1;
    while (j < css.length) {
        const ch = css[j];
        if (ch === quote)
            return token('string', i, j + 1);
        if (isNewline(ch))
            return token('bad-string', i, j, '', false);
        if (ch === '\\' && isNewline(css[j + 1])) {
            j += css[j + 1] === '\r' && css[j + 2] === '\n' ? 3 : 2;
            continue;
        }
        j = ch === '\\' ? readEscape(css, j)[1] : j + 1;
    }
    return token('string', i, css.length, '', false);
}
/** Read a number, a percentage or a dimension from `i`, where {@link startsNumber} holds (CSS Syntax 3 §4.3.3, §4.3.12). */
function readNumeric(css, i) {
    let j = i;
    if (css[j] === '+' || css[j] === '-')
        j += 1;
    while (isDigit(css[j]))
        j += 1;
    if (css[j] === '.' && isDigit(css[j + 1])) {
        j += 1;
        while (isDigit(css[j]))
            j += 1;
    }
    if ((css[j] === 'e' || css[j] === 'E') && (isDigit(css[j + 1]) || ((css[j + 1] === '+' || css[j + 1] === '-') && isDigit(css[j + 2])))) {
        j += 2;
        while (isDigit(css[j]))
            j += 1;
    }
    if (startsIdent(css, j)) {
        const [unit, end, cut] = readName(css, j);
        return token('dimension', i, end, unit, !cut);
    }
    if (css[j] === '%')
        return token('percentage', i, j + 1);
    return token('number', i, j);
}
/**
 * Read an ident, a function or a url token from `i`, where {@link startsIdent} holds (CSS Syntax 3
 * §4.3.4). The name `url` in any ASCII case, escapes decoded, with a `(` after it, makes a url token
 * unless the first character after the whitespace is a quote; then it is a function token.
 */
function readIdentLike(css, i) {
    const [name, j, cut] = readName(css, i);
    if (cut || css[j] !== '(')
        return token('ident', i, j, name, !cut);
    if (!/^[uU][rR][lL]$/.test(name))
        return token('function', i, j + 1, name);
    let k = j + 1;
    while (isCssWhitespace(css[k]) && isCssWhitespace(css[k + 1]))
        k += 1;
    const quoted = (c) => c === '"' || c === "'";
    if (quoted(css[k]) || (isCssWhitespace(css[k]) && quoted(css[k + 1])))
        return token('function', i, k, name);
    return readUrlToken(css, i, k);
}
/**
 * Read the rest of an unquoted url token whose `url(` starts at `start`; `k` is the first index after
 * it (CSS Syntax 3 §4.3.6). A `)` ends a url token. Whitespace not followed by `)`, a quote, a `(`, a
 * non-printable character or a `\` that starts no valid escape makes a bad url, which runs through
 * the first `)` that no escape holds (§4.3.14). The end of the input ends an unclean url.
 */
function readUrlToken(css, start, k) {
    while (isCssWhitespace(css[k]))
        k += 1;
    while (k < css.length) {
        const ch = css[k];
        if (ch === ')')
            return token('url', start, k + 1);
        if (isCssWhitespace(ch)) {
            while (isCssWhitespace(css[k]))
                k += 1;
            if (k >= css.length)
                break;
            if (css[k] === ')')
                return token('url', start, k + 1);
            return token('bad-url', start, badUrlEnd(css, k), '', false);
        }
        if (ch === '"' || ch === "'" || ch === '(' || isNonPrintable(ch) || (ch === '\\' && !isValidEscape(css, k))) {
            return token('bad-url', start, badUrlEnd(css, k + 1), '', false);
        }
        k = ch === '\\' ? readEscape(css, k)[1] : k + 1;
    }
    return token('url', start, css.length, '', false);
}
/** The index after the remnants of a bad url from `k`: through the first `)` no escape holds, or the end (CSS Syntax 3 §4.3.14). */
function badUrlEnd(css, k) {
    while (k < css.length) {
        if (css[k] === ')')
            return k + 1;
        k = isValidEscape(css, k) ? readEscape(css, k)[1] : k + 1;
    }
    return css.length;
}
/** True when an ident sequence starts at `i` (CSS Syntax 3 §4.3.9). */
function startsIdent(css, i) {
    const ch = css[i];
    if (ch === '-')
        return isNameStart(css[i + 1]) || css[i + 1] === '-' || isValidEscape(css, i + 1);
    return isNameStart(ch) || isValidEscape(css, i);
}
/** True when a number starts at `i` (CSS Syntax 3 §4.3.10). */
function startsNumber(css, i) {
    const ch = css[i];
    if (ch === '+' || ch === '-')
        return isDigit(css[i + 1]) || (css[i + 1] === '.' && isDigit(css[i + 2]));
    if (ch === '.')
        return isDigit(css[i + 1]);
    return isDigit(ch);
}
/**
 * Read the run of name code points and valid escapes that starts at `i` (CSS Syntax 3 §4.3.11).
 * Returns `[decodedName, indexAfterRun, cut]`; `cut` is true when the run ends with a `\` at the end
 * of the input, which the browser decodes as U+FFFD and which a character after it would change.
 */
function readName(css, i) {
    let j = i;
    let name = '';
    let cut = false;
    while (j < css.length) {
        if (isNameCodePoint(css[j])) {
            name += css[j];
            j += 1;
        }
        else if (isValidEscape(css, j)) {
            cut = j + 1 === css.length;
            const [text, next] = readEscape(css, j);
            name += text;
            j = next;
        }
        else {
            break;
        }
    }
    return [name, j, cut];
}
/**
 * Read the escape that starts with the `\` at `i` (CSS Syntax 3 §4.3.7). Returns
 * `[decodedText, indexAfterEscape]`. One to six hex digits give a code point, and one whitespace
 * after them belongs to the escape (`\r\n` counts as one). A hex value of 0, a surrogate or a value
 * above U+10FFFF decodes to U+FFFD, and so does a `\` at the end of the input. Any other character
 * after the `\` is the decoded text itself. The caller checks {@link isValidEscape} first.
 */
function readEscape(css, i) {
    let j = i + 1;
    if (j >= css.length)
        return ['�', j];
    let hex = '';
    while (hex.length < 6 && /[0-9A-Fa-f]/.test(css[j] ?? '')) {
        hex += css[j];
        j += 1;
    }
    if (hex === '') {
        const text = String.fromCodePoint(css.codePointAt(j));
        return [text, j + text.length];
    }
    if (css[j] === '\r' && css[j + 1] === '\n')
        j += 2;
    else if (isCssWhitespace(css[j]))
        j += 1;
    const value = Number.parseInt(hex, 16);
    const valid = value !== 0 && (value < 0xd800 || value > 0xdfff) && value <= 0x10ffff;
    return [valid ? String.fromCodePoint(value) : '�', j];
}
/** True when a valid escape starts at `i`: a `\` with no newline after it (CSS Syntax 3 §4.3.8). */
function isValidEscape(css, i) {
    return css[i] === '\\' && !isNewline(css[i + 1]);
}
/** True for a newline as CSS Syntax 3 reads one before it tokenizes: `\n`, `\r` or `\f`. */
function isNewline(ch) {
    return ch === '\n' || ch === '\r' || ch === '\f';
}
/** True for CSS whitespace: a newline, a tab or a space (CSS Syntax 3 §4.2). */
function isCssWhitespace(ch) {
    return ch === ' ' || ch === '\t' || isNewline(ch);
}
/**
 * True when the decoded name `name` is a custom property name: it starts with `--` and has more than
 * two code units (CSS Variables 1 §2). The name `--` alone is reserved, and the browser refuses it as
 * a declaration. Pure.
 */
function isCustomPropertyName(name) {
    return name.length > 2 && name.startsWith('--');
}
/** True for an ASCII digit. */
function isDigit(ch) {
    return ch !== undefined && ch >= '0' && ch <= '9';
}
/**
 * True for a CSS name-start code point (CSS Syntax 3 §4.2): an ASCII letter, `_`, or a code unit
 * from U+0080 up. U+0000 counts too, because the browser replaces it with U+FFFD before it tokenizes.
 */
function isNameStart(ch) {
    if (ch === undefined)
        return false;
    const code = ch.charCodeAt(0);
    return (code >= 0x61 && code <= 0x7a) || (code >= 0x41 && code <= 0x5a) || code === 0x5f || code >= 0x80 || code === 0;
}
/** True for a CSS name code point: a name-start code point, a digit or `-` (CSS Syntax 3 §4.2). */
function isNameCodePoint(ch) {
    return isNameStart(ch) || isDigit(ch) || ch === '-';
}
/** True for a non-printable code point as CSS Syntax 3 §4.2 names it: U+0001 to U+0008, U+000B, U+000E to U+001F, U+007F. */
function isNonPrintable(ch) {
    const code = ch.charCodeAt(0);
    return (code >= 0x01 && code <= 0x08) || code === 0x0b || (code >= 0x0e && code <= 0x1f) || code === 0x7f;
}
/** The closer of a block opener, or `null` for any other token type. A function closes at `)`. */
function closerOf(type) {
    if (type === '{')
        return '}';
    if (type === '[')
        return ']';
    return type === '(' || type === 'function' ? ')' : null;
}
/**
 * Build the component-value tree of `tokens` (CSS Syntax 3 §5.4.7, §5.4.8). An opener starts a
 * block, and only the innermost block's own closer ends it. Any other closer is a preserved token, so
 * a `}` inside an open `(` is text, as the browser reads it (R-88). The walk is iterative, so a deep
 * input never exhausts the call stack. Pure.
 */
function parseNodes(tokens) {
    const root = [];
    const open = [];
    let current = root;
    for (const t of tokens) {
        const top = open[open.length - 1];
        if (top !== undefined && t.type === closerOf(top.open.type)) {
            top.close = t;
            open.pop();
            current = open[open.length - 1]?.children ?? root;
            continue;
        }
        if (closerOf(t.type) !== null) {
            const block = { kind: 'block', open: t, children: [], close: null };
            current.push(block);
            open.push(block);
            current = block.children;
            continue;
        }
        current.push({ kind: 'token', token: t });
    }
    return root;
}
function isTokenOf(node, type) {
    return node !== undefined && node.kind === 'token' && node.token.type === type;
}
function isBraceBlock(node) {
    return node !== undefined && node.kind === 'block' && node.open.type === '{';
}
/** The input index where `node` starts. */
function startOf(node) {
    return node.kind === 'token' ? node.token.start : node.open.start;
}
/** `nodes` without the whitespace tokens at either end. */
function trimWs(nodes) {
    let from = 0;
    let to = nodes.length;
    while (from < to && isTokenOf(nodes[from], 'ws'))
        from += 1;
    while (to > from && isTokenOf(nodes[to - 1], 'ws'))
        to -= 1;
    return nodes.slice(from, to);
}
/** The tokens of `nodes` in input order, each block with its opener and closer. Only a block with a closer reaches it. */
function flatten(nodes, into = []) {
    for (const node of nodes) {
        if (node.kind === 'token') {
            into.push(node.token);
        }
        else {
            into.push(node.open);
            flatten(node.children, into);
            into.push(node.close);
        }
    }
    return into;
}
// ── The serializer ──────────────────────────────────────────────────────────────────────────────
/**
 * The text of `tokens`, each token copied from the input, or its `raw` text when a rename made it.
 * Where two tokens were not neighbours in the input, because a comment or a dropped part stood between
 * them, or where either is a renamed token, and their texts would join into another token, an empty
 * comment goes between them. So `u/**\/rl(` stays the ident `u` and the function `rl(`, and
 * `.a/**\/.b` gives `.a.b`. Pure.
 */
function serialize(text, tokens) {
    let out = '';
    let prev = null;
    for (const t of tokens) {
        const rewritten = prev !== null && (prev.raw !== undefined || t.raw !== undefined);
        if (prev !== null && (prev.end !== t.start || rewritten) && tokensJoin(text, prev, t))
            out += '/**/';
        out += textOf(text, t);
        prev = t;
    }
    return out;
}
/** The text {@link serialize} writes for `t`: its `raw` text, else its span of the input. */
function textOf(text, t) {
    return t.raw ?? text.slice(t.start, t.end);
}
/** {@link serialize} over the tokens of `nodes`. */
function serializeNodes(text, nodes) {
    return serialize(text, flatten(nodes));
}
/**
 * True when the texts of `a` and then `b`, written with nothing between them, no longer read as `a`
 * and then `b`: the browser reads a longer first token, or a `/` delim and a leading `*` open a
 * comment. The tokenizer reads from a position without regard to what came before it, so a first
 * token that ends where `a` ended leaves `b` and every token after it as they were.
 */
function tokensJoin(text, a, b) {
    const left = textOf(text, a);
    const right = textOf(text, b);
    if (a.type === 'delim' && a.value === '/' && right.startsWith('*'))
        return true;
    return readToken(left + right, 0).end !== left.length;
}
// ── The walk ────────────────────────────────────────────────────────────────────────────────────
/**
 * The deepest block nesting the scoper keeps. Compiled Tailwind v4 nests under ten deep. A construct
 * that holds a deeper block is refused, so every recursion here stays under this bound.
 */
const MAX_BLOCK_DEPTH = 128;
/** The at-rules whose contents the scoper walks as a rule list (R-90). */
const GROUPING_AT_RULES = new Set(['media', 'supports', 'layer', 'container']);
/** The at-rules the scoper keeps whole (R-90): their blocks hold keyframe stops and descriptors, never an element selector. */
const WHOLE_AT_RULES = new Set(['keyframes', 'font-face', 'property']);
/** A whole compound that targets the document root, so it maps onto the scope root. */
const ROOT_SELECTORS = new Set([':root', 'html', ':host', 'body']);
/**
 * The platform polarity classes. The shell toggles `.dark` on `<html>`, an ancestor of the mount
 * root, so a polarity-led selector keeps the class as the ancestor and takes the scope after it.
 * `.dark [scope]{--x}` (specificity 0,2,0) then beats the light `[scope]{--x}` (0,1,0).
 */
const POLARITY_CLASSES = new Set(['.dark', '.light']);
/** No registration. */
const NO_PROPERTIES = new Map();
/**
 * Walk one stylesheet. `options` `null` is the count walk of {@link countStyleRules}: it keeps every
 * name, and its counts equal those of a walk with options, because a refused block counts as a kept
 * one does. When the walk renames, a first walk over the same tree collects the kept `@keyframes` and
 * `@font-face` names, so a reference above its declaration is renamed too; its output, rejections and
 * registrations go.
 */
function walkStylesheet(text, scope, options) {
    const nodes = parseNodes(tokenize(text));
    const app = options === null || options.system ? null : options.appId;
    const known = options?.properties ?? NO_PROPERTIES;
    const fresh = (keyframes, families) => ({
        text, scope, app, known, added: new Map(), keyframes, families, rejected: [], rulesIn: 0, stripped: 0,
    });
    const keyframes = new Set();
    const families = new Map();
    if (app !== null)
        walkRuleList(fresh(keyframes, families), nodes, true, 0);
    const state = fresh(keyframes, families);
    const css = walkRuleList(state, nodes, true, 0);
    return { css, rejections: byteOffsets(text, state.rejected), rulesIn: state.rulesIn, stripped: state.stripped, properties: state.added };
}
function reject(state, construct, index) {
    state.rejected.push({ construct, index });
    return '';
}
/**
 * Walk one rule list: the stylesheet, or the block of a kept grouping at-rule, at block depth
 * `depth`. Whitespace between rules goes; at the top level a `<!--` or `-->` goes too, as the browser
 * ignores it there. An at-keyword starts an at-rule, which ends at a `;` or after its `{}` block.
 * Anything else starts a style rule, whose prelude runs to its `{}` block (CSS Syntax 3 §5.4.1 to
 * §5.4.3): a `;` there is prelude text.
 */
function walkRuleList(state, nodes, topLevel, depth) {
    let out = '';
    let i = 0;
    while (i < nodes.length) {
        const node = nodes[i];
        const type = node.kind === 'token' ? node.token.type : null;
        if (type === 'ws' || (topLevel && (type === 'cdo' || type === 'cdc'))) {
            i += 1;
            continue;
        }
        const prelude = [];
        let block = null;
        const atRule = isTokenOf(node, 'at-keyword');
        let j = atRule ? i + 1 : i;
        while (j < nodes.length) {
            const next = nodes[j];
            j += 1;
            if (isBraceBlock(next)) {
                block = next;
                break;
            }
            if (atRule && isTokenOf(next, 'semicolon'))
                break;
            prelude.push(next);
        }
        out += atRule ? emitAtRule(state, node.token, prelude, block, depth) : emitStyleRule(state, prelude, block, depth);
        i = j;
    }
    return out;
}
/**
 * Find the first fault in `nodes` at block depth `depth`, in input order, or `null`. A fault is an
 * unclean token, a stray `)`, `]` or `}`, a block the input never closed, a block deeper than
 * {@link MAX_BLOCK_DEPTH}, a `{}` block when `braces` is false, or a token `forbid` names. A bad
 * string and a bad url name their own construct; any other fault has `construct: null`, and the
 * caller names its own class.
 */
function findFault(nodes, depth, braces, forbid) {
    for (const node of nodes) {
        if (node.kind === 'token') {
            const t = node.token;
            if (t.type === 'bad-string' || (t.type === 'string' && !t.clean))
                return { construct: 'bad-string', index: t.start };
            if (t.type === 'bad-url' || (t.type === 'url' && !t.clean))
                return { construct: 'bad-url', index: t.start };
            if (!t.clean || t.type === ')' || t.type === ']' || t.type === '}' || forbid(t))
                return { construct: null, index: t.start };
            continue;
        }
        if (node.close === null || depth + 1 > MAX_BLOCK_DEPTH || (!braces && node.open.type === '{') || forbid(node.open)) {
            return { construct: null, index: node.open.start };
        }
        const inner = findFault(node.children, depth + 1, braces, forbid);
        if (inner !== null)
            return inner;
    }
    return null;
}
/** The tokens an at-rule prelude may not hold. */
function forbiddenInAtPrelude(t) {
    return t.type === 'semicolon' || t.type === 'at-keyword' || t.type === 'cdo' || t.type === 'cdc';
}
/**
 * The delims a plain selector list may hold: the class dot, the universal and namespace signs, the
 * three combinators, the attribute operators and the `-` of an `an+b` argument. Any other delim
 * (`&`, `@`, `!`, `/`, `<`, a lone `\`) makes the list no plain selector list (R-90).
 */
const SELECTOR_DELIMS = new Set(['.', '*', '|', '>', '+', '~', '=', '^', '$', '-']);
/** The tokens a selector list may not hold (R-90): a delim outside {@link SELECTOR_DELIMS}, a `;`, an at-keyword, a url, a `<!--` or `-->`. */
function forbiddenInSelector(t) {
    return (t.type === 'delim' && !SELECTOR_DELIMS.has(t.value)) || forbiddenInAtPrelude(t) || t.type === 'url';
}
/** Nothing extra is forbidden. */
function forbidNothing() {
    return false;
}
/**
 * Emit one at-rule (R-90). The name must be written with no escape and be one of the seven kept
 * names, compared in ASCII lower case. The prelude must hold no fault and no `{}` block. Then:
 *
 * - `media`, `supports`, `container`: a block is required, and its contents are walked as a rule list.
 * - `layer`: with a block, the prelude is empty or one dotted name; without one, it is a list of
 *   dotted names, kept as a statement.
 * - `keyframes` (one ident or string), `font-face` (no prelude), `property` (one custom property
 *   name that is no design token, R-65): a block is required, and it is kept whole when it holds no
 *   fault.
 *
 * Anything else is refused: `at-rule`, or `bad-string` or `bad-url` at that token.
 */
function emitAtRule(state, at, prelude, block, depth) {
    const refuse = (construct, index) => {
        if (block !== null)
            state.rulesIn += 1;
        return reject(state, construct, index);
    };
    const raw = state.text.slice(at.start, at.end);
    const name = asciiLower(at.value);
    if (raw.includes('\\') || (!GROUPING_AT_RULES.has(name) && !WHOLE_AT_RULES.has(name)))
        return refuse('at-rule', at.start);
    const fault = findFault(prelude, depth, false, forbiddenInAtPrelude);
    if (fault !== null)
        return refuse(fault.construct ?? 'at-rule', fault.construct === null ? at.start : fault.index);
    const words = trimWs(prelude).filter((node) => !isTokenOf(node, 'ws'));
    const head = () => serialize(state.text, [at, ...flatten(trimTrailingWs(prelude))]);
    if (name === 'layer' && block === null) {
        return words.length > 0 && isLayerNameList(words, true) ? `${head()};` : refuse('at-rule', at.start);
    }
    if (block === null || depth + 1 > MAX_BLOCK_DEPTH)
        return refuse('at-rule', at.start);
    if (GROUPING_AT_RULES.has(name)) {
        if (name === 'layer' && !isLayerNameList(words, false))
            return refuse('at-rule', at.start);
        return `${head()}{${walkRuleList(state, block.children, false, depth + 1)}}`;
    }
    if (!isWholePrelude(state.text, name, words))
        return refuse('at-rule', at.start);
    const bodyFault = findFault(block.children, depth + 1, true, forbidNothing);
    if (bodyFault !== null)
        return refuse(bodyFault.construct ?? 'at-rule', bodyFault.construct === null ? at.start : bodyFault.index);
    // isWholePrelude admitted exactly one ident or string as the prelude of a `keyframes` or a `property`.
    const word = words[0];
    if (name === 'keyframes') {
        const keyframesName = word.token.type === 'ident' ? word.token.value : decodeString(state.text, word.token);
        state.keyframes.add(keyframesName);
        state.rulesIn += 1;
        const renamed = state.app === null ? head() : `${textOf(state.text, at)} ${serializeIdent(confinedName(state, keyframesName))}`;
        return `${renamed}{${serializeNodes(state.text, block.children)}}`;
    }
    if (name === 'property' && !keepsRegistration(state, word.token.value, registrationText(state.text, block.children))) {
        return refuse('at-rule', at.start);
    }
    const children = name === 'font-face' && state.app !== null ? confineFontFace(state, block.children) : block.children;
    if (children === null)
        return refuse('at-rule', at.start);
    state.rulesIn += 1;
    return `${head()}{${serializeNodes(state.text, children)}}`;
}
/** `text` with each ASCII upper-case letter in lower case, as CSS compares keywords and at-rule names. */
function asciiLower(text) {
    return text.replace(/[A-Z]/g, (letter) => letter.toLowerCase());
}
/**
 * The separator between the app slug and a confined name (R-94, WS-40 architecture-1 and security-1).
 * It is outside the slug alphabet `[a-z0-9-]` of `RESOURCE_SLUG_PATTERN`, so the first `_` of a new name
 * ends the slug. A `-` there gave app `acme` with `tools-spin` and app `acme-tools` with `spin` the one
 * name `acme-tools-spin`.
 */
const CONFINED_NAME_SEPARATOR = '_';
/**
 * The decoded document-global name `<appId>_<name>` that an app's `@keyframes` or `@font-face` name
 * `name` takes (R-94). The caller escapes it. The walk calls it only when it renames, so `state.app`
 * holds the slug.
 */
function confinedName(state, name) {
    return `${state.app}${CONFINED_NAME_SEPARATOR}${name}`;
}
/** A token node whose token keeps the span of `t` and writes `raw` (R-94). */
function renamedToken(t, type, raw, end = t.end) {
    return { kind: 'token', token: { type, start: t.start, end, value: '', clean: true, raw } };
}
/**
 * Decide one `@property` whose decoded name is `name` and whose registration text is `text` (R-94),
 * and record it. Without an app name (the system stylesheet and the count walk) every rule stays, and
 * `state.added` takes `text` for `name`, so the last rule of a name sets it, as in the browser (S5a
 * WS-40 security-3). With an app name, the rule stays when `name` starts with
 * {@link PROPERTY_REGISTRATION_PREFIX} and the document's registration in force of `name`, else this
 * walk's first one, is absent or equal to `text`. A kept app rule whose name neither map holds joins
 * `state.added`.
 */
function keepsRegistration(state, name, text) {
    if (state.app === null) {
        state.added.set(name, text);
        return true;
    }
    const inForce = state.known.get(name) ?? state.added.get(name);
    if (!name.startsWith(PROPERTY_REGISTRATION_PREFIX) || (inForce !== undefined && inForce !== text))
        return false;
    if (inForce === undefined)
        state.added.set(name, text);
    return true;
}
/**
 * The registration text of a `@property` block (R-94): its tokens with every comment gone, each
 * whitespace run as one space, the whitespace beside a `:` or a `;` and at either end gone, and the
 * trailing `;` gone. So `{ syntax : "*" ; inherits:false; }` and `{syntax:"*";inherits:false}` give
 * the same text, and `{inherits:FALSE}` does not.
 */
function registrationText(text, children) {
    const tokens = flatten(children);
    const kept = [];
    const edge = (t) => t === undefined || t.type === 'colon' || t.type === 'semicolon';
    tokens.forEach((t, i) => {
        if (t.type !== 'ws')
            kept.push(t);
        else if (!edge(kept[kept.length - 1]) && !edge(tokens[i + 1]))
            kept.push({ ...t, raw: ' ' });
    });
    while (kept.length > 0 && kept[kept.length - 1].type === 'semicolon')
        kept.pop();
    return serialize(text, kept);
}
/**
 * The block of a kept app `@font-face` with each `font-family` descriptor renamed to the string
 * `"<appId>_<family>"` (R-94), or `null` when the block must go. It goes when it holds a `{}` block,
 * because a brace changes where a browser ends a descriptor, or when a `font-family` value is no
 * family name ({@link familyOf}). Each `font-family` descriptor takes the rename. Only the family of the
 * last one joins `state.families`, because the browser keeps the last descriptor of a block (S5a WS-40
 * architecture-7). A block that goes adds none, so no reference moves to a family that no kept rule
 * declares (S5a WS-40 architecture-2). A descriptor name is decoded and compared in ASCII lower case,
 * as the browser compares it.
 */
function confineFontFace(state, children) {
    if (children.some(isBraceBlock))
        return null;
    const out = [];
    const declared = [];
    let part = [];
    const flush = () => {
        const k = part.findIndex((node) => !isTokenOf(node, 'ws'));
        const first = part[k];
        const colon = part.findIndex((node, i) => i > k && !isTokenOf(node, 'ws'));
        if (k === -1 || !isTokenOf(first, 'ident') || asciiLower(first.token.value) !== 'font-family' || !isTokenOf(part[colon], 'colon')) {
            out.push(...part);
            return true;
        }
        const value = part.slice(colon + 1);
        const from = value.findIndex((node) => !isTokenOf(node, 'ws'));
        const core = trimWs(value);
        const family = familyOf(state.text, core);
        if (family === null)
            return false;
        declared.push(family);
        const firstToken = core[0].token;
        const lastToken = core[core.length - 1].token;
        const renamed = renamedToken(firstToken, 'string', serializeString(confinedName(state, family)), lastToken.end);
        out.push(...part.slice(0, colon + 1), ...value.slice(0, from), renamed, ...value.slice(from + core.length));
        return true;
    };
    for (const node of children) {
        if (!isTokenOf(node, 'semicolon')) {
            part.push(node);
            continue;
        }
        if (!flush())
            return null;
        out.push(node);
        part = [];
    }
    if (!flush())
        return null;
    const family = declared[declared.length - 1];
    if (family !== undefined && !state.families.has(asciiLower(family)))
        state.families.set(asciiLower(family), family);
    return out;
}
/** The CSS-wide keywords and `default`, which no `<custom-ident>` and no unquoted family name may be. */
const CSS_WIDE_KEYWORDS = new Set(['initial', 'inherit', 'unset', 'revert', 'revert-layer', 'default']);
/**
 * The generic font families (CSS Fonts 4), and `-webkit-body`, which Chromium 151 reads as a generic
 * family too: an unquoted one names a generic family, never a font face. Chromium drops an
 * `@font-face` that names one, so a rename would make a dead face live (S5a WS-40 architecture-9).
 */
const GENERIC_FAMILIES = new Set([
    '-webkit-body',
    'serif',
    'sans-serif',
    'cursive',
    'fantasy',
    'monospace',
    'system-ui',
    'emoji',
    'math',
    'fangsong',
    'ui-serif',
    'ui-sans-serif',
    'ui-monospace',
    'ui-rounded',
]);
/**
 * The family name `nodes` writes, or `null` (CSS Fonts 4 `<family-name>`): one string, decoded, or a
 * run of idents with optional whitespace between them, decoded and joined by one space. A run that
 * holds a CSS-wide keyword, and a lone ident that is a generic family, name no family: `null`.
 */
function familyOf(text, nodes) {
    const only = nodes[0];
    if (nodes.length === 1 && isTokenOf(only, 'string'))
        return decodeString(text, only.token);
    const words = [];
    for (const node of nodes) {
        if (node.kind !== 'token')
            return null;
        const t = node.token;
        if (t.type === 'ws')
            continue;
        if (t.type !== 'ident' || CSS_WIDE_KEYWORDS.has(asciiLower(t.value)))
            return null;
        words.push(t.value);
    }
    if (words.length === 0 || (words.length === 1 && GENERIC_FAMILIES.has(asciiLower(words[0]))))
        return null;
    return words.join(' ');
}
/**
 * The decoded value of a clean string token (CSS Syntax 3 §4.3.5): the text between its quotes, with
 * each escaped newline gone and each other escape decoded.
 */
function decodeString(text, t) {
    let out = '';
    let j = t.start + 1;
    const end = t.end - 1;
    while (j < end) {
        if (text[j] !== '\\') {
            out += text[j];
            j += 1;
        }
        else if (isNewline(text[j + 1])) {
            j += text[j + 1] === '\r' && text[j + 2] === '\n' ? 3 : 2;
        }
        else {
            const [decoded, next] = readEscape(text, j);
            out += decoded;
            j = next;
        }
    }
    return out;
}
/**
 * `name` written as a CSS ident that decodes back to `name` (CSSOM, serialize an identifier): U+0000
 * as U+FFFD, a control character and a leading digit (or a digit after a leading `-`) as a hex escape,
 * and any other ASCII character outside a letter, a digit, `-` and `_` escaped. Every caller passes a
 * {@link confinedName}, which holds the `_` separator, so `name` is never the lone `-` that CSSOM
 * writes as `\-`.
 */
function serializeIdent(name) {
    const chars = Array.from(name);
    return chars
        .map((ch, i) => {
        const code = ch.codePointAt(0);
        if (code === 0)
            return '\uFFFD';
        if ((code >= 0x01 && code <= 0x1f) || code === 0x7f || (i === 0 && isDigit(ch)) || (i === 1 && isDigit(ch) && chars[0] === '-')) {
            return `\\${code.toString(16)} `;
        }
        return isNameCodePoint(ch) ? ch : `\\${ch}`;
    })
        .join('');
}
/**
 * `value` written as a CSS string that decodes back to `value` (CSSOM, serialize a string): double
 * quotes, U+0000 as U+FFFD, a control character as a hex escape, and the quote and the backslash escaped.
 */
function serializeString(value) {
    let out = '"';
    for (const ch of value) {
        const code = ch.codePointAt(0);
        if (code === 0)
            out += '\uFFFD';
        else if ((code >= 0x01 && code <= 0x1f) || code === 0x7f)
            out += `\\${code.toString(16)} `;
        else
            out += ch === '"' || ch === '\\' ? `\\${ch}` : ch;
    }
    return `${out}"`;
}
/** `nodes` without the whitespace tokens at its end. */
function trimTrailingWs(nodes) {
    let to = nodes.length;
    while (to > 0 && isTokenOf(nodes[to - 1], 'ws'))
        to -= 1;
    return nodes.slice(0, to);
}
/**
 * True when `words`, the prelude of a `@layer` with its whitespace removed, is a list of layer names:
 * idents joined by `.`, the names joined by `,` when `list` is true. An empty list is true when
 * `list` is false, the anonymous block form.
 */
function isLayerNameList(words, list) {
    let expectName = true;
    for (const word of words) {
        if (expectName) {
            if (!isTokenOf(word, 'ident'))
                return false;
            expectName = false;
        }
        else if ((isTokenOf(word, 'delim') && word.token.value === '.') || (list && isTokenOf(word, 'comma'))) {
            expectName = true;
        }
        else {
            return false;
        }
    }
    return !expectName || words.length === 0;
}
/**
 * True when `words`, read from `text`, is the prelude the whole at-rule `name` takes: for `keyframes`
 * one ident or one string whose decoded name is not empty and, in ASCII lower case, is neither `none`
 * nor one of the {@link CSS_WIDE_KEYWORDS}; nothing for `font-face`; and for `property` one custom property name
 * whose decoded form {@link isDesignTokenName} refuses. A browser drops the ident form
 * `@keyframes none` and keeps the string form `@keyframes "none"`. In both forms a rename would make
 * the rule live under a keyword that `animation: none` or `animation-name: inherit` writes (S5a WS-40
 * architecture-5). A browser drops `@keyframes ""` and reads `animation: ""` as `none`, so a rename of
 * the empty name would start an animation the app text never ran (S5a WS-40 architecture-8). A `@property` on a design token re-registers the token for the whole document, so
 * it goes (R-65).
 */
function isWholePrelude(text, name, words) {
    if (name === 'font-face')
        return words.length === 0;
    if (words.length !== 1)
        return false;
    const word = words[0];
    if (word.kind !== 'token' || (word.token.type !== 'ident' && (name !== 'keyframes' || word.token.type !== 'string')))
        return false;
    const value = word.token.type === 'string' ? decodeString(text, word.token) : word.token.value;
    if (name === 'keyframes')
        return value !== '' && asciiLower(value) !== 'none' && !CSS_WIDE_KEYWORDS.has(asciiLower(value));
    return isCustomPropertyName(value) && !isDesignTokenName(value);
}
/**
 * Emit one style rule (R-90, R-65, R-107). A prelude with no block goes as `incomplete-rule`. An empty
 * prelude goes as `selector` and counts nothing. Any other prelude counts one rule and must be a
 * plain selector list: no fault, no `{}` block, none of the tokens {@link forbiddenInSelector} names,
 * and each comma-separated selector non-empty with no leading or trailing `>`, `+` or `~`. A trailing
 * combinator goes because the boundary after it would make a dead selector live. The body is then
 * emitted by {@link emitBody}; a token block the strip emptied goes whole and counts as stripped.
 */
function emitStyleRule(state, prelude, block, depth) {
    const items = trimWs(prelude);
    // The walk starts a rule at a node that is no whitespace, so a rule with no block has a prelude.
    if (block === null)
        return reject(state, 'incomplete-rule', startOf(items[0]));
    if (items.length === 0)
        return reject(state, 'selector', block.open.start);
    state.rulesIn += 1;
    const fault = findFault(items, depth, false, forbiddenInSelector);
    if (fault !== null)
        return reject(state, fault.construct ?? 'selector', fault.construct === null ? startOf(items[0]) : fault.index);
    const selectors = splitAtCommas(items).map(trimWs);
    if (selectors.some((sel) => sel.length === 0 || isCombinator(sel[0]) || isCombinator(sel[sel.length - 1]))) {
        return reject(state, 'selector', startOf(items[0]));
    }
    const scoped = [...new Set(selectors.map((sel) => scopeSelector(state, sel)))].join(',');
    const tokenBlock = selectors.every((sel) => isRootOrPolarity(state.text, sel));
    const body = emitBody(state, block.children, depth + 1, tokenBlock);
    if (body === null) {
        state.stripped += 1;
        return '';
    }
    return `${scoped}{${body}}`;
}
/** Split `nodes` at its comma tokens; a comma inside a block is inside that block's node. */
function splitAtCommas(nodes) {
    const parts = [[]];
    for (const node of nodes) {
        if (isTokenOf(node, 'comma'))
            parts.push([]);
        else
            parts[parts.length - 1].push(node);
    }
    return parts;
}
/** True for a combinator delim: `>`, `+` or `~`. */
function isCombinator(node) {
    return isTokenOf(node, 'delim') && (node.token.value === '>' || node.token.value === '+' || node.token.value === '~');
}
/** A selector split at its first whitespace or combinator: the leading compound, its lowercased text, and the rest. */
function splitCompound(text, selector) {
    let h = 0;
    while (h < selector.length && !isTokenOf(selector[h], 'ws') && !isCombinator(selector[h]))
        h += 1;
    const head = serializeNodes(text, selector.slice(0, h));
    return { head, key: head.toLowerCase(), rest: selector.slice(h) };
}
/** True for the key of a document-root compound (`:root`, `html`, `:host`, `:host(...)`, `body`). */
function isRootKey(key) {
    return ROOT_SELECTORS.has(key) || key.startsWith(':host(');
}
/** True when `selector` is one bare document-root compound or one bare polarity class, so it maps onto the scope root itself. */
function isRootOrPolarity(text, selector) {
    const { key, rest } = splitCompound(text, selector);
    return rest.length === 0 && (isRootKey(key) || POLARITY_CLASSES.has(key));
}
/**
 * Scope one selector of a plain list. A leading root compound is replaced by the scope root, a
 * leading polarity class keeps the scope after it, and any other selector becomes a descendant of the
 * scope root. A root or polarity compound with a sibling combinator after it takes the descendant
 * form, so it matches nothing outside the app. A root token with a qualifier (`:root[dir=rtl]`) is no
 * bare root compound and takes the descendant form too, a safe no-op. Each form then takes
 * {@link nestedSurfaceBoundary} on its subject compound (R-107), through {@link bounded}.
 */
function scopeSelector(state, selector) {
    const { head, key, rest } = splitCompound(state.text, selector);
    const firstOfRest = trimWs(rest)[0];
    const sibling = isTokenOf(firstOfRest, 'delim') && (firstOfRest.token.value === '~' || firstOfRest.token.value === '+');
    if (!sibling && isRootKey(key))
        return `${state.scope}${bounded(state, rest)}`;
    if (!sibling && POLARITY_CLASSES.has(key))
        return `${head} ${state.scope}${bounded(state, rest)}`;
    return `${state.scope} ${bounded(state, selector)}`;
}
/** The legacy pseudo-elements a single colon writes (Selectors 4 §16.1). */
const LEGACY_PSEUDO_ELEMENTS = new Set(['before', 'after', 'first-line', 'first-letter']);
/**
 * The text of `nodes`, the tail of a scoped selector, with {@link nestedSurfaceBoundary} in its
 * subject compound: the nodes after the last whitespace or combinator. The boundary goes before the
 * subject's first pseudo-element, a `::` or a single `:` with a {@link LEGACY_PSEUDO_ELEMENTS} name in
 * any ASCII case, because a pseudo-class after a pseudo-element makes the whole selector invalid. With
 * no pseudo-element it goes at the end; with no nodes it follows the scope root itself.
 */
function bounded(state, nodes) {
    let subject = 0;
    nodes.forEach((node, i) => {
        if (isTokenOf(node, 'ws') || isCombinator(node))
            subject = i + 1;
    });
    let at = subject;
    while (at < nodes.length && !startsPseudoElement(nodes, at))
        at += 1;
    return `${serializeNodes(state.text, nodes.slice(0, at))}${nestedSurfaceBoundary(state.scope)}${serializeNodes(state.text, nodes.slice(at))}`;
}
/** True when a pseudo-element starts at `nodes[i]`: a colon, then a colon or a legacy pseudo-element name. */
function startsPseudoElement(nodes, i) {
    const next = nodes[i + 1];
    if (!isTokenOf(nodes[i], 'colon') || next?.kind !== 'token')
        return false;
    return next.token.type === 'colon' || (next.token.type === 'ident' && LEGACY_PSEUDO_ELEMENTS.has(asciiLower(next.token.value)));
}
/**
 * Split a rule body into its parts as a browser with CSS nesting reads it (CSS Syntax 3 §5.4.4,
 * §5.4.6). A part that starts with an ident, optional whitespace and a colon is a declaration. A
 * custom property declaration (its decoded name passes {@link isCustomPropertyName}) runs to the next
 * `;`, `{}` blocks included. The reserved name `--` is no custom property name, so its part ends at a
 * `{}` block, as the browser reads it. Any other part runs to the next `;` or through a `{}` block: with a block it is a nested
 * rule, which also takes one `;` right after its block. Without one it is a declaration when it
 * started as one, a nested rule when it starts with an at-keyword, whitespace when it holds only
 * whitespace, and invalid otherwise. A `;` that ends a part opens the next one, which may be empty.
 */
function splitBody(children) {
    const segments = [];
    let i = 0;
    for (;;) {
        let k = i;
        while (isTokenOf(children[k], 'ws'))
            k += 1;
        const first = children[k];
        let c = k + 1;
        while (isTokenOf(children[c], 'ws'))
            c += 1;
        const declaration = isTokenOf(first, 'ident') && isTokenOf(children[c], 'colon');
        const custom = declaration && isCustomPropertyName(first.token.value);
        let j = k;
        while (j < children.length && !isTokenOf(children[j], 'semicolon') && (custom || !isBraceBlock(children[j])))
            j += 1;
        if (isBraceBlock(children[j])) {
            const end = isTokenOf(children[j + 1], 'semicolon') ? j + 2 : j + 1;
            segments.push({ kind: 'rule', nodes: children.slice(i, j + 1) });
            i = end;
            if (i >= children.length)
                return segments;
            continue;
        }
        const nodes = children.slice(i, j);
        if (declaration)
            segments.push({ kind: 'declaration', name: first.token.value, nodes });
        else if (j === k)
            segments.push({ kind: 'space', nodes });
        else
            segments.push({ kind: isTokenOf(first, 'at-keyword') ? 'rule' : 'invalid', nodes });
        if (j >= children.length)
            return segments;
        i = j + 1;
    }
}
/**
 * Emit a rule body at block depth `depth`: each kept part, joined with `;`. Whitespace parts stay, so
 * a body with nothing dropped keeps its bytes, comments aside. A nested rule goes as `nested-rule`, an
 * invalid part as `declaration`. In a token block a declaration whose decoded name is a design token
 * goes silently (R-65). Any other declaration stays unless it holds a fault (a `{}` block counts as
 * one outside a custom property): then it goes as `declaration`, or as `bad-string` or `bad-url` at
 * that token. Returns `null` when the strip removed a token and only whitespace is left.
 */
function emitBody(state, children, depth, tokenBlock) {
    const kept = [];
    let strippedToken = false;
    for (const segment of splitBody(children)) {
        const start = trimWs(segment.nodes)[0];
        if (segment.kind === 'space') {
            kept.push(serializeNodes(state.text, segment.nodes));
            continue;
        }
        if (segment.kind !== 'declaration') {
            reject(state, segment.kind === 'rule' ? 'nested-rule' : 'declaration', startOf(start));
            continue;
        }
        const name = segment.name;
        if (tokenBlock && isDesignTokenName(name)) {
            strippedToken = true;
            continue;
        }
        const fault = findFault(segment.nodes, depth, isCustomPropertyName(name), forbidNothing);
        if (fault !== null) {
            reject(state, fault.construct ?? 'declaration', fault.construct === null ? startOf(start) : fault.index);
            continue;
        }
        kept.push(serializeNodes(state.text, state.app === null ? segment.nodes : renameReferences(state, name, segment.nodes)));
    }
    if (strippedToken && kept.every((part) => part.trim() === ''))
        return null;
    return kept.join(';');
}
/**
 * The nodes of one kept declaration named `name` (decoded), with its references to this stylesheet's
 * kept `@keyframes` and `@font-face` names renamed (R-94). `animation`, `animation-name` and a custom
 * property rename each top-level ident or string that names a kept `@keyframes` to the escaped ident
 * `<appId>_<name>`. `font-family` and a custom property rename each comma-separated family
 * ({@link familyOf}) that names a kept family, in ASCII lower case, to the string
 * `"<appId>_<family>"` with the family's first declared spelling. A trailing `!important` stays as
 * written in both arms: outside the family, and never renamed as a keyframes name. Any other
 * declaration comes back as it was.
 */
function renameReferences(state, name, nodes) {
    const property = isCustomPropertyName(name) ? '--' : asciiLower(name);
    const keyframes = property === '--' || property === 'animation' || property === 'animation-name';
    const families = property === '--' || property === 'font-family';
    if (!keyframes && !families)
        return nodes;
    // A declaration part starts with its name, optional whitespace and a colon.
    const colon = nodes.findIndex((node) => isTokenOf(node, 'colon'));
    let value = nodes.slice(colon + 1);
    if (families)
        value = renameFamilies(state, value);
    if (keyframes) {
        // The `important` of a trailing `!important` is the flag, never a keyframes name (S5a WS-40 architecture-6).
        const tail = value.findIndex((_node, i) => importantAt(value, i));
        value = value.map((node, i) => {
            if ((tail !== -1 && i > tail) || node.kind !== 'token' || node.token.raw !== undefined)
                return node;
            const t = node.token;
            const named = t.type === 'ident' ? t.value : t.type === 'string' ? decodeString(state.text, t) : null;
            return named !== null && state.keyframes.has(named) ? renamedToken(t, 'ident', serializeIdent(confinedName(state, named))) : node;
        });
    }
    return [...nodes.slice(0, colon + 1), ...value];
}
/** True for the `!important` tail: a `!` delim, optional whitespace, and the ident `important` in any ASCII case. */
function importantAt(nodes, i) {
    const bang = nodes[i];
    if (!isTokenOf(bang, 'delim') || bang.token.value !== '!')
        return false;
    const rest = trimWs(nodes.slice(i + 1));
    const word = rest[0];
    return rest.length === 1 && isTokenOf(word, 'ident') && asciiLower(word.token.value) === 'important';
}
/** `value` with each comma-separated family that names a kept `@font-face` family renamed (R-94). */
function renameFamilies(state, value) {
    const out = [];
    let item = [];
    const flush = () => {
        const tail = item.findIndex((_node, i) => importantAt(item, i));
        const body = tail === -1 ? item : item.slice(0, tail);
        const from = body.findIndex((node) => !isTokenOf(node, 'ws'));
        const core = trimWs(body);
        const family = core.length === 0 ? null : familyOf(state.text, core);
        const declared = family === null ? undefined : state.families.get(asciiLower(family));
        if (declared === undefined) {
            out.push(...item);
            return;
        }
        const first = core[0].token;
        const last = core[core.length - 1].token;
        out.push(...item.slice(0, from), renamedToken(first, 'string', serializeString(confinedName(state, declared)), last.end), ...item.slice(from + core.length));
    };
    for (const node of value) {
        if (!isTokenOf(node, 'comma')) {
            item.push(node);
            continue;
        }
        flush();
        out.push(node);
        item = [];
    }
    flush();
    return out;
}
/**
 * Turn the code-unit index of each rejection into the byte offset of that character in the UTF-8 form
 * of `text`. A surrogate pair is four bytes; a lone surrogate is three, the U+FFFD an encoder writes
 * for it. The rejections come in input order, so one pass over `text` serves them all.
 */
function byteOffsets(text, rejected) {
    let bytes = 0;
    let at = 0;
    return rejected.map(({ construct, index }) => {
        while (at < index) {
            const code = text.charCodeAt(at);
            const low = text.charCodeAt(at + 1);
            if (code >= 0xd800 && code <= 0xdbff && low >= 0xdc00 && low <= 0xdfff) {
                bytes += 4;
                at += 2;
                continue;
            }
            bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : 3;
            at += 1;
        }
        return { construct, offset: bytes };
    });
}
//# sourceMappingURL=scope-css.js.map